<template>
    <div class="mb-2">
        <div :class="`row ${phaseString}-accordion`">
            <button
                type="button"
                class="btn"
                data-toggle="collapse"
                :data-target="`#collapse-${phaseString}`"
                :class="{'collapsed': currentPhase !== phase}"
                :aria-expanded="currentPhase === phase"
                :aria-controls="`collapse-${phaseString}`"
            >
                <svg
                    class="up"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                >
                    <path
                        d="M177 159.7l136 136c9.4 9.4 9.4 24.6 0 33.9l-22.6 22.6c-9.4 9.4-24.6 9.4-33.9 0L160 255.9l-96.4 96.4c-9.4 9.4-24.6 9.4-33.9 0L7 329.7c-9.4-9.4-9.4-24.6 0-33.9l136-136c9.4-9.5 24.6-9.5 34-.1z"
                    />
                </svg>
                <svg
                    class="down"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                >
                    <path
                        d="M143 352.3L7 216.3c-9.4-9.4-9.4-24.6 0-33.9l22.6-22.6c9.4-9.4 24.6-9.4 33.9 0l96.4 96.4 96.4-96.4c9.4-9.4 24.6-9.4 33.9 0l22.6 22.6c9.4 9.4 9.4 24.6 0 33.9l-136 136c-9.2 9.4-24.4 9.4-33.8 0z"
                    />
                </svg>
                <h5>{{ $t(phaseString) }}</h5>
            </button>
        </div>
        <div
            :id="`collapse-${phaseString}`"
            :class="[{'show': currentPhase === phase}, `row collapse ${phaseString}-accordion`]"
        >
            <phase-header :background-class="`${phaseString}-odd`" />
            <div class="col-12">
                <div
                    v-for="(value, index) in list"
                    class="row item pt-2 pb-2"
                    :class="index % 2 === 1 ? `${phaseString}-odd`: ''"
                    :key="index"
                >
                    <div class="col-1">
                        {{ value.outageMilestoneId }}.
                    </div>
                    <div class="col-4">
                        {{ value.description }}
                    </div>
                    <div class="col-2">
                        {{ getTimeframeLabel(value.timeframe) }}
                        <a
                            v-if="shouldShowInfo(value)"
                            role="button"
                            data-toggle="popover"
                            data-placement="bottom"
                            data-content="Calculated based on the latest date of outage approval."
                        >
                            <font-awesome-icon icon="info-circle" />
                        </a>
                    </div>
                    <div
                        class="col-2 input-group input-group-sm"
                        v-if="value.inputType === kpiInputTypes.number"
                    >
                        <input
                            type="number"
                            v-model.number="value.numericValue"
                            min="0"
                            max="100"
                            @blur="enforcePercentage(value)"
                            class="form-control from-control-sm"
                        >
                        <div class="input-group-append">
                            <span class="input-group-text">%</span>
                        </div>
                    </div>
                    <div
                        class="col-2"
                        v-if="value.inputType === kpiInputTypes.checkbox"
                    >
                        <input
                            type="checkbox"
                            v-model="value.booleanValue"
                            :value="value.booleanValue"
                            :id="`${phaseString}-${index}`"
                        >
                    </div>
                    <div class="col-2">
                        <date-picker
                            v-model="value.completionDate"
                            type="date"
                            value-type="format"
                            format="YYYY-MM-DD"
                            :disabled-date="disabledAfterToday"
                            v-if="!value.canHaveMaxScoreAnyTime && value.booleanValue"
                            :input-class="[{'is-invalid': value.booleanValue && !value.completionDate || getValidateObject('outageScorecardValues[' + (value.outageMilestoneId-1) +'].CompletionDate')},'form-control form-control-sm']"
                        />
                        <div
                            v-if="getValidateObject('outageScorecardValues[' + (value.outageMilestoneId-1) +'].CompletionDate')"
                            class="invalid-feedback d-block"
                        >
                            <span
                                v-for="error in getValidateObject('outageScorecardValues[' + (value.outageMilestoneId-1) +'].CompletionDate')"
                                :key="error"
                            >{{ error }}</span>
                        </div>
                        <date-picker
                            v-model="value.additionalDate"
                            type="date"
                            value-type="format"
                            format="YYYY-MM-DD"
                            v-if="value.hasAdditionalDate && value.booleanValue"
                            :input-class="[{'is-invalid': value.booleanValue && !value.additionalDate},'form-control form-control-sm']"
                        />
                        <small
                            v-if="value.hasAdditionalDate && value.booleanValue"
                            style="white-space: nowrap;"
                        >
                            Latest date of outage approval</small>
                    </div>
                    <div class="col-1">
                        {{ value.score != null ? value.score.toFixed(0) + ' %' : '' }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-this-alias */
/* eslint-disable no-undef */

import Vue from 'vue';
import { Prop } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { EventBus, DataInputHelper } from '@/utils';
import { OutageScorecardItemValue } from '@/utils/interfaces';
import { PHASES, KPI_INPUT_TYPES } from '@/utils/scorecard-constants';
import PhaseHeaderComponent from './phase-header-component.vue'

@Component({
    components: {
        'phase-header': PhaseHeaderComponent
    }
})
export default class MilestoneAccordionComponent extends Vue {
    private phases = PHASES;
    private kpiInputTypes = KPI_INPUT_TYPES;
    private validate: any = {};

    @Prop()
    private list!: OutageScorecardItemValue[];

    @Prop()
    private phaseString!: string;

    @Prop()
    private currentPhase!: number;

    @Prop()
    private phase!: number;

    constructor() {
        super();

        this.list.forEach((value) => {
            this.validate['outageScorecardValues[' + (value.outageMilestoneId - 1) + '].CompletionDate'] = null;
        });

        EventBus.$on(EventBus.OUTAGE_SCORECARD.VALIDATION_ERROR, this.handleValidationError);
    }

    mounted(): void {
        ($('[data-toggle="popover"]') as any).popover();
    }

    private beforeDestroy(): void {
        EventBus.$off(EventBus.OUTAGE_SCORECARD.VALIDATION_ERROR, this.handleValidationError);
    }

    private enforcePercentage(value: any): void {
        if (!value || !value.numericValue || isNaN(value.numericValue)) {
            value.numericValue = 0;
        }
        value.numericValue = value.numericValue < 0 ? 0 : value.numericValue > 100 ? 100 : value.numericValue;
    }

    private getTimeframeLabel(value: number): string {
        if (value === 0)
            return this.$t('throughoutOutage').toString();

        return value.toString();
    }

    private getValidateObject(name: string): void {
        return DataInputHelper.getValidateObject(name, this.validate);
    }

    private disabledAfterToday(date: Date): boolean {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return date > today;
    }

    private shouldShowInfo(value: OutageScorecardItemValue): boolean {
        return (value.outageMilestoneId === 7 || value.outageMilestoneId === 8) && value.timeframe < -180;
    }

    private handleValidationError (validationData: any): void {
        Object.keys(validationData).forEach(key => {
            this.validate[key] = validationData[key];
        });
    }
}
</script>

<style>
@import url('./scorecard.less');

</style>
